<template>
	<article class="new-event-hotel-result-card">
		<header class="new-event-hotel-result-card__info">
			<router-link
				class="new-event-hotel-result-card__image-link"
				:to="{name: 'hotel-info', params: {hotelId: eventHotel.id, shortName: event.shortName}}"
				target="_blank"
			>
				<img :src="hotelImage" :alt="eventHotel.name">
			</router-link>

			<div class="new-event-hotel-result-card__details">
				<div class="new-event-hotel-result-card__heading">
					<h3>{{eventHotel.name}}</h3>

					<div
						v-if="event.showHotelStars"
						class="new-event-hotel-result-card__stars"
						:aria-label="`${eventHotel.starRating || 0} estrelas`"
					>
						<span v-for="star in stars" :key="star">&#9733;</span>
					</div>
				</div>

				<div class="new-event-hotel-result-card__meta">
					<div class="new-event-hotel-result-card__location">
						<p>
							<span>{{translation.location}}</span>
							{{eventHotel.district || ''}}
						</p>
						<p>
							<span>{{translation.eventDistance}}</span>
							{{hotelDistance}}
						</p>
					</div>

					<img
						v-if="hotelStamp"
						class="new-event-hotel-result-card__stamp"
						:src="hotelStamp"
						:alt="hotelStampTooltip"
						v-b-tooltip.hover
						:title="hotelStampTooltip"
					>
				</div>
			</div>
		</header>

		<p v-if="hotelDescription" class="new-event-hotel-result-card__observation">
			<span>OBS:</span> {{hotelDescription}}
		</p>

		<div class="new-event-hotel-result-card__rooms">
			<div
				v-for="entry in roomRows"
				:key="entry.key"
				class="new-event-hotel-result-card__room-wrapper"
				:class="{'new-event-hotel-result-card__room-wrapper--open': isRoomOpen(entry)}"
			>
				<div class="new-event-hotel-result-card__room">
					<h4 :title="roomTypeTitle(entry.roomType)">
						{{roomTypeName(entry.roomType)}}
					</h4>

					<p>{{mealPlan(entry.price.mealPlan)}}</p>

					<strong>{{formatCurrency(roomDailyPrice(entry.price), entry.price.priceCurrency)}}</strong>

					<div class="new-event-hotel-result-card__action">
						<button
							v-if="isAvailableAllotment(entry.price.allotment)"
							type="button"
							@click="openBooking(entry)"
						>
							{{lowercaseLabel(translation.book)}}
						</button>
						<span v-else>{{translation.soldOut}}</span>
					</div>
				</div>

				<hotel-booking-personal-info
					v-if="isRoomOpen(entry) && bookingRequest && pax"
					:booking-request="bookingRequest"
					:pax="pax"
					:countries="countries"
					@back="$emit('close-booking')"
					@continue="$emit('file-request', $event)"
				/>
			</div>
		</div>
	</article>
</template>

<script>
import { translate } from '../../assets/translations';
import { formatDate } from '../../assets/format';
import { getDailyPriceWithoutTaxes, getTax } from '../../util';
import HotelBookingPersonalInfo from './HotelBookingPersonalInfo.vue';
import Pax from '../../model/Pax';
import PriceOrigin from '../../enums/PriceOrigin';
import { EventStamp } from '../../enums/EventStamp';

const FALLBACK_HOTEL_IMAGE = 'https://media.omnibees.com/Images/3795/Property/247128.jpg';

export default {
	components: {
		HotelBookingPersonalInfo,
	},
	props: {
		activeBookingKey: {
			type: String,
			default: '',
		},
		countries: {
			type: Array,
			default: () => [],
		},
		criteria: {
			type: Object,
			required: true,
		},
		event: {
			type: Object,
			required: true,
		},
		eventHotel: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			bookingRequest: null,
			pax: null,
		};
	},
	computed: {
		hotelDescription() {
			const translation = this.hotelTranslation();
			return translation && translation.description ? translation.description : '';
		},
		hotelDistance() {
			const eventDistance = this.eventHotel.eventInfo ? this.eventHotel.eventInfo.eventDistance : null;
			if (eventDistance === 0) return '0 KM';
			if (!eventDistance) return '--';
			return `${eventDistance} KM`;
		},
		hotelImage() {
			return this.eventHotel.facadeImagePath || FALLBACK_HOTEL_IMAGE;
		},
		hotelStamp() {
			const stamp = this.eventHotel.eventInfo ? this.eventHotel.eventInfo.stamp : null;
			if (!stamp || !EventStamp[stamp]) return '';
			return `/static/stamps/${this.$store.state.language.lang}/${EventStamp[stamp].src}`;
		},
		hotelStampTooltip() {
			const stamp = this.eventHotel.eventInfo ? this.eventHotel.eventInfo.stamp : null;
			const eventStamps = translate(this.$store.state.language).eventStamps || {};
			return stamp && eventStamps[stamp] ? eventStamps[stamp] : '';
		},
		roomRows() {
			const rows = [];
			(this.eventHotel.roomTypes || []).forEach(roomType => {
				(roomType.prices || []).forEach(price => {
					rows.push({
						key: this.roomKey(roomType, price),
						roomType,
						price,
					});
				});
			});
			return rows;
		},
		stars() {
			const starRating = Number(this.eventHotel.starRating) || 0;
			return [...Array(starRating)].map((value, index) => index + 1);
		},
		translation() {
			return translate(this.$store.state.language).hotelPrices;
		},
	},
	methods: {
		buildBookingRequest(entry) {
			return {
				clientId: this.event.client.id,
				adultsQty: Number(this.criteria.adultsQty) || 1,
				chdAges: this.criteria.chdAges,
				checkin: formatDate(this.criteria.checkin, 'DD/MM/YYYY'),
				checkout: formatDate(this.criteria.checkout, 'DD/MM/YYYY'),
				locale: this.$store.state.language.lang,
				hotel: this.eventHotel,
				roomType: entry.roomType,
				rooms: this.buildRooms(entry.roomType),
				price: entry.price,
				mealPlan: entry.price.mealPlan,
				obs: null,
			};
		},
		buildGuests() {
			const guests = [];
			const adultsQty = Number(this.criteria.adultsQty) || 1;
			for (let index = 0; index < adultsQty; index++) {
				guests.push({ name: null, RPH: null });
			}
			return guests;
		},
		buildRooms(roomType) {
			const rooms = [];
			const roomsQty = Number(this.criteria.roomsQty) || 1;
			for (let index = 0; index < roomsQty; index++) {
				rooms.push({
					roomTypeName: roomType.name,
					roomTypeId: roomType.id,
					guests: this.buildGuests(),
				});
			}
			return rooms;
		},
		formatCurrency(value, currency) {
			const symbol = currency && currency.symbol ? currency.symbol : 'R$';
			const number = this.normalizeCurrencyValue(value).toLocaleString('pt-BR', {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			});
			return `${symbol} ${number}`;
		},
		hotelTranslation() {
			if (!this.eventHotel.eventInfo || !this.eventHotel.eventInfo.translations) return {};
			const languageId = this.$store.state.language.id;
			return this.eventHotel.eventInfo.translations.find(translation => translation.language.id === languageId)
				|| this.eventHotel.eventInfo.translations[0]
				|| {};
		},
		isAvailableAllotment(allotment) {
			if (!allotment) return false;
			return allotment.freeSale || allotment.availableUnits >= Number(this.criteria.roomsQty || 1);
		},
		isRoomOpen(entry) {
			return this.activeBookingKey === entry.key;
		},
		lowercaseLabel(label) {
			return String(label || '').toLowerCase();
		},
		mealPlan(meal) {
			return meal ? this.translation.breakfastIncluded : this.translation.breakfastNotIncluded;
		},
		normalizeCurrencyValue(value) {
			if (value === null || value === undefined || value === '') return 0;
			if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

			let normalizedValue = String(value).trim().replace(/[^\d,.-]/g, '');
			if (!normalizedValue) return 0;

			const lastComma = normalizedValue.lastIndexOf(',');
			const lastDot = normalizedValue.lastIndexOf('.');

			if (lastComma !== -1 && lastDot !== -1) {
				normalizedValue = lastComma > lastDot
					? normalizedValue.replace(/\./g, '').replace(',', '.')
					: normalizedValue.replace(/,/g, '');
			} else if (lastComma !== -1) {
				normalizedValue = normalizedValue.replace(',', '.');
			} else if (/^-?\d{1,3}(\.\d{3})+$/.test(normalizedValue)) {
				normalizedValue = normalizedValue.replace(/\./g, '');
			}

			const numberValue = Number(normalizedValue);
			return Number.isFinite(numberValue) ? numberValue : 0;
		},
		openBooking(entry) {
			this.bookingRequest = this.buildBookingRequest(entry);
			this.pax = new Pax();
			this.$emit('open-booking', entry.key);
		},
		roomDailyPrice(price) {
			return getDailyPriceWithoutTaxes(price.dailyPricePerRoom, getTax(price.dailyPricePerRoom));
		},
		roomKey(roomType, price) {
			return [
				this.eventHotel.id,
				roomType.id,
				price.id || price.priceId || price.supplierId || price.price,
			].join('-');
		},
		roomTypeName(roomType) {
			const languageId = this.$store.state.language.id;
			const translation = roomType.translations
				? roomType.translations.find(item => item.language.id === languageId)
				: null;
			return translation && translation.name ? translation.name : roomType.name;
		},
		roomTypeTitle(roomType) {
			const firstPrice = roomType.prices && roomType.prices.length ? roomType.prices[0] : {};
			return firstPrice.supplierId === PriceOrigin.OMNIBEES.id ? `-${roomType.name}` : roomType.name;
		},
	},
};
</script>

<style scoped lang="scss">
.new-event-hotel-result-card {
	box-sizing: border-box;
	overflow: hidden;
	border: 1px solid #d7d7d7;
	border-radius: 35px;
	background: #ffffff;
	color: #002c41;
}

.new-event-hotel-result-card__info {
	display: grid;
	grid-template-columns: 272px minmax(0, 1fr);
	gap: 42px;
	align-items: start;
	min-height: 259px;
	padding: 25px 25px 30px 34px;
}

.new-event-hotel-result-card__image-link,
.new-event-hotel-result-card__image-link img {
	display: block;
	width: 272px;
	height: 203px;
}

.new-event-hotel-result-card__image-link img {
	border-radius: 25px;
	object-fit: cover;
}

.new-event-hotel-result-card__details {
	display: grid;
	grid-template-rows: auto 1fr;
	min-width: 0;
	padding-top: 16px;
}

.new-event-hotel-result-card__heading,
.new-event-hotel-result-card__meta {
	display: flex;
	justify-content: space-between;
	gap: 24px;
	min-width: 0;
}

.new-event-hotel-result-card__heading {
	align-items: flex-start;
}

.new-event-hotel-result-card__meta {
	align-items: flex-start;
	margin-top: 75px;
}

.new-event-hotel-result-card__heading h3 {
	min-width: 0;
	margin: 0;
	color: #002c41;
	font-size: 25px;
	font-weight: 700;
	line-height: 29px;
}

.new-event-hotel-result-card__location {
	min-width: 0;
}

.new-event-hotel-result-card__location p {
	margin: 0;
	color: #5a5a5a;
	font-size: 16px;
	font-weight: 400;
	line-height: 25px;
}

.new-event-hotel-result-card__location span {
	display: inline-block;
	min-width: 111px;
}

.new-event-hotel-result-card__stars {
	display: flex;
	flex: 0 0 auto;
	justify-content: flex-end;
	gap: 1px;
	color: #ff9600;
	font-size: 24px;
	line-height: 1;
}

.new-event-hotel-result-card__stamp {
	display: block;
	flex: 0 0 auto;
	max-width: 168px;
	max-height: 80px;
	object-fit: contain;
}

.new-event-hotel-result-card__observation {
	margin: 0 34px 28px;
	color: #5a5a5a;
	font-size: 15px;
	font-weight: 400;
	line-height: 24px;
}

.new-event-hotel-result-card__observation span {
	color: #002c41;
	font-weight: 700;
}

.new-event-hotel-result-card__rooms {
	border-top: 1px solid #d7d7d7;
}

.new-event-hotel-result-card__room-wrapper + .new-event-hotel-result-card__room-wrapper {
	border-top: 1px solid #d7d7d7;
}

.new-event-hotel-result-card__room-wrapper--open + .new-event-hotel-result-card__room-wrapper {
	border-top: 0;
}

.new-event-hotel-result-card__room {
	display: grid;
	grid-template-columns: minmax(220px, 2.1fr) minmax(175px, 1.1fr) minmax(140px, .85fr) minmax(150px, .65fr);
	align-items: center;
	min-height: 71px;
}

.new-event-hotel-result-card__room h4,
.new-event-hotel-result-card__room p,
.new-event-hotel-result-card__room strong,
.new-event-hotel-result-card__action {
	display: flex;
	align-items: center;
	box-sizing: border-box;
	min-height: 71px;
	margin: 0;
	padding: 18px 23px;
}

.new-event-hotel-result-card__room p,
.new-event-hotel-result-card__room strong,
.new-event-hotel-result-card__action {
	border-left: 1px solid #d7d7d7;
}

.new-event-hotel-result-card__room h4 {
	color: #002c41;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
}

.new-event-hotel-result-card__room p {
	color: #5a5a5a;
	font-size: 14px;
	font-weight: 400;
	line-height: 16px;
}

.new-event-hotel-result-card__room strong {
	color: #002c41;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
}

.new-event-hotel-result-card__action {
	justify-content: center;
}

.new-event-hotel-result-card__action button {
	box-sizing: border-box;
	width: 154px;
	height: 34px;
	border: 1px solid #ff9600;
	border-radius: 10px;
	background: #ff9600;
	color: #ffffff;
	font-family: inherit;
	font-size: 16px;
	font-weight: 700;
	line-height: 19px;
	text-align: center;
	box-shadow: 4px 4px 100px rgba(0, 0, 0, .1);
	cursor: pointer;
}

.new-event-hotel-result-card__action span {
	color: #d70000;
	font-size: 14px;
	font-weight: 400;
	line-height: 16px;
	text-transform: uppercase;
}

@media (max-width: 1100px) {
	.new-event-hotel-result-card__info {
		grid-template-columns: 220px 1fr;
	}

	.new-event-hotel-result-card__heading,
	.new-event-hotel-result-card__meta {
		flex-wrap: wrap;
	}

	.new-event-hotel-result-card__meta {
		margin-top: 42px;
	}

	.new-event-hotel-result-card__image-link,
	.new-event-hotel-result-card__image-link img {
		width: 220px;
		height: 164px;
	}

	.new-event-hotel-result-card__room {
		grid-template-columns: 1.5fr 1fr .85fr .7fr;
	}
}

@media (max-width: 900px) {
	.new-event-hotel-result-card {
		border-radius: 20px;
	}

	.new-event-hotel-result-card__info {
		grid-template-columns: 151px minmax(0, 1fr);
		gap: 0 15px;
		min-height: 0;
		padding: 27px 28px 18px;
	}

	.new-event-hotel-result-card__image-link,
	.new-event-hotel-result-card__image-link img {
		width: 151px;
		height: 103px;
	}

	.new-event-hotel-result-card__details {
		display: contents;
	}

	.new-event-hotel-result-card__heading {
		grid-column: 2;
		grid-row: 1;
		display: block;
		min-width: 0;
		margin-top: 11px;
	}

	.new-event-hotel-result-card__heading h3 {
		margin: 0 0 8px;
		font-size: 20px;
		line-height: 23px;
	}

	.new-event-hotel-result-card__stars {
		justify-content: flex-start;
		font-size: 20px;
	}

	.new-event-hotel-result-card__meta {
		grid-column: 1 / -1;
		grid-row: 2;
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 16px;
		margin-top: 24px;
	}

	.new-event-hotel-result-card__stamp {
		max-width: 78px;
	}

	.new-event-hotel-result-card__location p {
		font-size: 16px;
		line-height: 25px;
	}

	.new-event-hotel-result-card__location span {
		display: block;
		min-width: 0;
	}

	.new-event-hotel-result-card__observation {
		margin: 0 28px 20px;
		font-size: 14px;
		line-height: 22px;
	}

	.new-event-hotel-result-card__rooms {
		margin: 0 28px 24px;
		border-top: 1px solid #d7d7d7;
	}

	.new-event-hotel-result-card__room {
		grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr) minmax(72px, .75fr);
		align-items: stretch;
	}

	.new-event-hotel-result-card__room h4,
	.new-event-hotel-result-card__room p,
	.new-event-hotel-result-card__room strong {
		align-items: flex-start;
		min-height: 70px;
		padding: 24px 9px 13px;
	}

	.new-event-hotel-result-card__room h4 {
		font-size: 16px;
		line-height: 19px;
	}

	.new-event-hotel-result-card__room p {
		border-top: 0;
		border-left: 1px solid #d7d7d7;
		font-size: 14px;
		line-height: 16px;
	}

	.new-event-hotel-result-card__room strong {
		border-top: 0;
		border-left: 1px solid #d7d7d7;
		font-size: 16px;
		line-height: 19px;
	}

	.new-event-hotel-result-card__action {
		grid-column: 1 / -1;
		justify-content: center;
		min-height: 58px;
		padding: 10px 0 12px;
		border-top: 0;
		border-left: 0;
	}

	.new-event-hotel-result-card__action button {
		width: 100%;
		height: 36px;
	}

	.new-event-hotel-result-card__action span {
		display: flex;
		justify-content: center;
		width: 100%;
		padding-top: 10px;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/hotel-reservation/RoomTypePrice.vue